"use client";

import { memo, useCallback, useEffect, useRef, useMemo } from "react";
import { cn } from "@/lib/utils";
import { animate } from "framer-motion";
type Theme = "default" | "dusk" | "warm" | "oceanic" | "sunset" | "neon" | "midnight" | "forest" | "lavender" | "cosmic" | "neutral" | "zinc" | "white";

// Theme color palettes
const THEME_COLORS = {
  default: {
    colors: ["#dd7bbb", "#d79f1e", "#5a922c", "#4c7894", "#dd7bbb"]
  },
  dusk: {
    colors: ["#7e22ce", "#c4b5fd", "#a78bfa", "#f5d0fe", "#8b5cf6"]
  },
  warm: {
    colors: ["#f59e0b", "#fcd34d", "#fbbf24", "#fb923c", "#ef4444"]
  },
  oceanic: {
    colors: ["#0891b2", "#22d3ee", "#06b6d4", "#67e8f9", "#0284c7"]
  },
  sunset: {
    colors: ["#f97316", "#c2410c", "#fb923c", "#fca5a5", "#ef4444"]
  },
  neon: {
    colors: ["#059669", "#22d3ee", "#a3e635", "#fcd34d", "#4f46e5"]
  },
  midnight: {
    colors: ["#1e40af", "#1d4ed8", "#3b82f6", "#0f172a", "#6366f1"]
  },
  forest: {
    colors: ["#15803d", "#16a34a", "#22c55e", "#4ade80", "#065f46"]
  },
  lavender: {
    colors: ["#7c3aed", "#8b5cf6", "#a78bfa", "#c4b5fd", "#6d28d9"]
  },
  cosmic: {
    colors: ["#312e81", "#4338ca", "#818cf8", "#c7d2fe", "#6d28d9"]
  },
  neutral: {
    colors: ["#525252", "#737373", "#a3a3a3", "#d4d4d4", "#171717"]
  },
  zinc: {
    colors: ["#52525b", "#71717a", "#a1a1aa", "#d4d4d8", "#18181b"]
  },
  white: {
    colors: ["#ffffff", "#f5f5f5", "#e5e5e5", "#d4d4d4", "#a3a3a3"]
  }
};
interface GlowingEffectProps {
  blur?: number;
  inactiveZone?: number;
  proximity?: number;
  spread?: number;
  variant?: "default" | "white";
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  movementDuration?: number;
  borderWidth?: number;
  theme?: Theme;
}
const GlowingEffect = memo(({
  blur = 0,
  inactiveZone = 0.7,
  proximity = 0,
  spread = 20,
  variant = "default",
  glow = false,
  className,
  movementDuration = 2,
  borderWidth = 1,
  disabled = true,
  theme = "default"
}: GlowingEffectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastPosition = useRef({
    x: 0,
    y: 0
  });
  const animationFrameRef = useRef<number>(0);

  // Generate gradient based on theme
  const gradient = useMemo(() => {
    // Get colors for the selected theme or fall back to default
    const themeColors = THEME_COLORS[theme]?.colors || THEME_COLORS.default.colors;
    if (variant === "white") {
      return `repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          var(--black),
          var(--black) calc(25% / var(--repeating-conic-gradient-times))
        )`;
    }

    // Create the gradient with the theme colors
    return `
        radial-gradient(circle, ${themeColors[0]} 10%, ${themeColors[0]}00 20%),
        radial-gradient(circle at 40% 40%, ${themeColors[1]} 5%, ${themeColors[1]}00 15%),
        radial-gradient(circle at 60% 60%, ${themeColors[2]} 10%, ${themeColors[2]}00 20%), 
        radial-gradient(circle at 40% 60%, ${themeColors[3]} 10%, ${themeColors[3]}00 20%),
        repeating-conic-gradient(
          from 236.84deg at 50% 50%,
          ${themeColors[0]} 0%,
          ${themeColors[1]} calc(25% / var(--repeating-conic-gradient-times)),
          ${themeColors[2]} calc(50% / var(--repeating-conic-gradient-times)), 
          ${themeColors[3]} calc(75% / var(--repeating-conic-gradient-times)),
          ${themeColors[4] || themeColors[0]} calc(100% / var(--repeating-conic-gradient-times))
        )
      `;
  }, [theme, variant]);
  const handleMove = useCallback((e?: MouseEvent | {
    x: number;
    y: number;
  }) => {
    if (!containerRef.current) return;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(() => {
      const element = containerRef.current;
      if (!element) return;
      const {
        left,
        top,
        width,
        height
      } = element.getBoundingClientRect();
      const mouseX = e?.x ?? lastPosition.current.x;
      const mouseY = e?.y ?? lastPosition.current.y;
      if (e) {
        lastPosition.current = {
          x: mouseX,
          y: mouseY
        };
      }
      const center = [left + width * 0.5, top + height * 0.5];
      const distanceFromCenter = Math.hypot(mouseX - center[0], mouseY - center[1]);
      const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;
      if (distanceFromCenter < inactiveRadius) {
        element.style.setProperty("--active", "0");
        return;
      }
      const isActive = mouseX > left - proximity && mouseX < left + width + proximity && mouseY > top - proximity && mouseY < top + height + proximity;
      element.style.setProperty("--active", isActive ? "1" : "0");
      if (!isActive) return;
      const currentAngle = parseFloat(element.style.getPropertyValue("--start")) || 0;
      let targetAngle = 180 * Math.atan2(mouseY - center[1], mouseX - center[0]) / Math.PI + 90;
      const angleDiff = (targetAngle - currentAngle + 180) % 360 - 180;
      const newAngle = currentAngle + angleDiff;
      animate(currentAngle, newAngle, {
        duration: movementDuration,
        ease: [0.16, 1, 0.3, 1],
        onUpdate: value => {
          element.style.setProperty("--start", String(value));
        }
      });
    });
  }, [inactiveZone, proximity, movementDuration]);
  useEffect(() => {
    if (disabled) return;
    const handleScroll = () => handleMove();
    const handlePointerMove = (e: PointerEvent) => handleMove(e);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    document.body.addEventListener("pointermove", handlePointerMove, {
      passive: true
    });
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener("scroll", handleScroll);
      document.body.removeEventListener("pointermove", handlePointerMove);
    };
  }, [handleMove, disabled]);
  return <>
        <div className={cn("pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity", glow && "opacity-100", variant === "white" && "border-white", disabled && "!block")} />
        <div ref={containerRef} style={{
      "--blur": `${blur}px`,
      "--spread": spread,
      "--start": "0",
      "--active": "0",
      "--glowingeffect-border-width": `${borderWidth}px`,
      "--repeating-conic-gradient-times": "5",
      "--gradient": gradient,
      "--black": "#000"
    } as React.CSSProperties} className={cn("pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity", glow && "opacity-100", blur > 0 && "blur-[var(--blur)] ", className, disabled && "!hidden")}>
          <div className={cn("glow", "rounded-[inherit]", 'after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))] after:rounded-[inherit] after:content-[""]', "after:[border:var(--glowingeffect-border-width)_solid_transparent]", "after:[background-attachment:fixed] after:[background:var(--gradient)]", "after:opacity-[var(--active)] after:transition-opacity after:duration-300", "after:[mask-clip:padding-box,border-box]", "after:[mask-composite:intersect]", "after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]")} />
        </div>
      </>;
});
GlowingEffect.displayName = "GlowingEffect";
export { GlowingEffect };